import React from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { Pressable } from '../pressable'
import { GLYPH_COLORS, PlusGlyph } from '../primitive-glyphs/glyphs'
import { Text } from '../text'

export type AttachmentButtonProps = {
    /** Primary label next to the plus icon. */
    label?: string
    /** Format hint shown below (e.g. accepted file types). */
    hint?: string
    onPress?: () => void
    isDisabled?: boolean
    className?: string
    testID?: string
}

const labelText = 'font-normal text-[0.875rem] leading-[1.25rem] text-content-default'
const hintText = 'text-center text-[0.8125rem] leading-[1.25rem] text-content-muted'

/**
 * Attachment upload trigger matching the Figma redesign (AOS-599): a 124px
 * square dashed-less tile with a plus + label and a format hint. Opens the
 * host's file picker via `onPress`. Hover strengthens the border; disabled
 * dims the tile.
 */
export const AttachmentButton: React.FC<AttachmentButtonProps> = ({
    label = 'Add',
    hint = 'jpg, jpeg, png, mp4, pdf',
    onPress,
    isDisabled,
    className,
    testID,
}) => (
    <Pressable
        className={twMerge(
            'size-[124px] flex-col items-center justify-center gap-1 rounded p-3 ' +
                'border border-border-subtle bg-background-elevation ' +
                'hover:border-border-strong hover:bg-background-surface-hover',
            isDisabled ? 'opacity-50' : '',
            className,
        )}
        onPress={isDisabled ? undefined : onPress}
        accessibilityRole='button'
        accessibilityLabel={label}
        testID={testID}
    >
        <Box className='flex-row items-center justify-center gap-1'>
            <PlusGlyph size={24} color={GLYPH_COLORS.default} />
            <Text className={labelText}>{label}</Text>
        </Box>
        <Text className={hintText}>{hint}</Text>
    </Pressable>
)

AttachmentButton.displayName = 'AttachmentButton'
